import { useEffect, useState } from "react";

import { DataTable } from "../../components/DataTable";

import {
  Category,
  CreateCategoryRequest
} from "../../interfaces/Category";

import { categoryService } from "../../services/categoryService";

export function CreateCategoryPage() {

  // =========================================
  // STATES
  // =========================================

  const API_PATH =
    process.env.REACT_APP_API_PATH;

  const [categories, setCategories] =
    useState<Category[]>([]);

  const [categoryName, setCategoryName] =
    useState("");

  const [categorySlug, setCategorySlug] =
    useState("");

  const [description, setDescription] =
    useState("");

  const [displayOrder, setDisplayOrder] =
    useState(1);

  const [isFeatured, setIsFeatured] =
    useState(false);

  const [status, setStatus] =
    useState("active");

  const [metaTitle, setMetaTitle] =
    useState("");

  const [metaDescription, setMetaDescription] =
    useState("");

  const [metaKeywords, setMetaKeywords] =
    useState("");

  const [canonicalUrl, setCanonicalUrl] =
    useState("");

  const [ogTitle, setOgTitle] =
    useState("");

  const [ogDescription, setOgDescription] =
    useState("");

  const [schemaMarkup, setSchemaMarkup] =
    useState("");

  const [categoryImage, setCategoryImage] =
    useState<File | null>(null);

  const [ogImage, setOgImage] =
    useState<File | null>(null);

  const [editingCategoryId, setEditingCategoryId] =
    useState<number | null>(null);

  const [viewCategory, setViewCategory] =
    useState<Category | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] =
    useState<number | null>(null);

  const [infoModal, setInfoModal] = useState<{ msg: string; type: "success" | "error" | "warn" } | null>(null);
  // =========================================
  // LOAD CATEGORY
  // =========================================

  async function loadCategories() {

    try {

      const data =
        await categoryService.getAll();

      const sortedData =
        [...data].sort(
          (a, b) =>
            b.categoryID - a.categoryID
        );

      setCategories(sortedData);

    } catch (error) {

      console.error(error);
    }
  }

  useEffect(() => {

    loadCategories();

  }, []);

  // =========================================
  // AUTO SLUG
  // =========================================

  function handleNameChange(
    value: string
  ) {

    setCategoryName(value);

    setCategorySlug(
      value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
    );
  }

  // =========================================
  // RESET FORM
  // =========================================

  function resetForm() {

    setCategoryName("");
    setCategorySlug("");
    setDescription("");
    setDisplayOrder(1);
    setIsFeatured(false);
    setStatus("active");
    setEditingCategoryId(null);
    setMetaTitle("");
    setMetaDescription("");
    setMetaKeywords("");
    setCanonicalUrl("");

    setOgTitle("");
    setOgDescription("");
    setSchemaMarkup("");

    setCategoryImage(null);
    setOgImage(null);
  }

  // =========================================
  // CREATE CATEGORY
  // =========================================

  async function handleSubmit() {

    try {

      const payload: CreateCategoryRequest = {

        categoryName,
        categorySlug,
        description,

        displayOrder,

        isFeatured,

        status,

        metaTitle,
        metaDescription,
        metaKeywords,

        canonicalUrl,

        ogTitle,
        ogDescription,

        schemaMarkup,

        categoryImage,
        ogImage
      };

      let response;

      // =====================================
      // UPDATE
      // =====================================

      if (editingCategoryId !== null) {

        response =
          await categoryService.update({

            categoryID:
              editingCategoryId,

            ...payload
          });

      } else {

        // =====================================
        // CREATE
        // =====================================

        response =
          await categoryService.create(
            payload
          );
      }

      setInfoModal({ msg: response.message, type: "success" });

      await loadCategories();

      resetForm();

    } catch (error: any) {

      console.error(error);

      setInfoModal({ msg: error.message || "Error saving category", type: "error" });
    }
  }

  // =========================================
  // DELETE CATEGORY
  // =========================================

  async function deleteCategory() {

    if (!deleteConfirmId) {
      return;
    }

    try {

      const response =
        await categoryService.delete(
          deleteConfirmId
        );

      setInfoModal({ msg: response.message, type: "success" });

      await loadCategories();

      setDeleteConfirmId(null);

    } catch (error) {

      console.error(error);

      setInfoModal({ msg: "Error deleting category", type: "error" });
    }
  }



  // =========================================
  // EDIT CATEGORY
  // =========================================

  function editCategory(item: Category) {

    setEditingCategoryId(item.categoryID);

    setCategoryName(item.categoryName);

    setCategorySlug(item.categorySlug);

    setDescription(item.description || "");

    setDisplayOrder(item.displayOrder);

    setIsFeatured(item.isFeatured);

    setStatus(item.status || "active");

    setMetaTitle(item.metaTitle || "");

    setMetaDescription(item.metaDescription || "");

    setMetaKeywords(item.metaKeywords || "");

    setCanonicalUrl(item.canonicalUrl || "");

    setOgTitle(item.ogTitle || "");

    setOgDescription(item.ogDescription || "");

    setSchemaMarkup(item.schemaMarkup || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }

  // =========================================
  // TOGGLE STATUS
  // =========================================

  async function toggleStatus(
    item: Category
  ) {

    try {

      await categoryService.update({

        categoryID:
          item.categoryID,

        categoryName:
          item.categoryName,

        categorySlug:
          item.categorySlug,

        description:
          item.description,

        displayOrder:
          item.displayOrder,

        isFeatured:
          item.isFeatured,

        status:
          item.status === "active"
            ? "inactive"
            : "active",

        metaTitle:
          item.metaTitle,

        metaDescription:
          item.metaDescription,

        metaKeywords:
          item.metaKeywords,

        canonicalUrl:
          item.canonicalUrl,

        ogTitle:
          item.ogTitle,

        ogDescription:
          item.ogDescription,

        schemaMarkup:
          item.schemaMarkup
      });

      await loadCategories();

    } catch (error) {

      console.error(error);
    }
  }

  return (

    <div className="d-grid gap-4">

      {/* ========================================= */}
      {/* FORM */}
      {/* ========================================= */}

      <div className="card border-0 shadow-sm mst-card">

        <div className="card-body p-4">

          <div className="d-flex align-items-center gap-2 mb-4">

            <div className="mst-icon-box">
              <i className="bi bi-tags"></i>
            </div>

            <div>

              <h5 className="mb-0 fw-bold mst-title">
                Create Category
              </h5>

              <small className="mst-subtitle">
                Add Category
              </small>

            </div>
          </div>

          <div className="row g-3">

            {/* CATEGORY NAME */}

            <div className="col-md-6">

              <label className="form-label fw-semibold">
                Category Name
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="Category Name"
                value={categoryName}
                onChange={(e) =>
                  handleNameChange(
                    e.target.value
                  )
                }
              />
            </div>

            {/* SLUG */}

            <div className="col-md-6">

              <label className="form-label fw-semibold">
                Category Slug
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="category-slug"
                value={categorySlug}
                onChange={(e) =>
                  setCategorySlug(
                    e.target.value
                  )
                }
              />
            </div>

            {/* DESCRIPTION */}

            <div className="col-md-12">

              <label className="form-label fw-semibold">
                Description
              </label>

              <textarea
                className="form-control"
                rows={3}
                value={description}
                onChange={(e) =>
                  setDescription(
                    e.target.value
                  )
                }
              />
            </div>

            {/* IMAGE */}

            <div className="col-md-6">

              <label className="form-label fw-semibold">
                Category Image
              </label>

              <input
                type="file"
                className="form-control"
                onChange={(e) =>
                  setCategoryImage(
                    e.target.files?.[0] || null
                  )
                }
              />
            </div>

            {/* DISPLAY ORDER */}

            <div className="col-md-3">

              <label className="form-label fw-semibold">
                Display Order
              </label>

              <input
                type="number"
                className="form-control"
                value={displayOrder}
                onChange={(e) =>
                  setDisplayOrder(
                    Number(e.target.value)
                  )
                }
              />
            </div>

            {/* STATUS */}

            <div className="col-md-3">

              <label className="form-label fw-semibold">
                Status
              </label>

              <select
                className="form-select"
                value={status}
                onChange={(e) =>
                  setStatus(
                    e.target.value
                  )
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="inactive">
                  Inactive
                </option>
              </select>
            </div>

            {/* FEATURED */}

            <div className="col-md-3">

              <div className="form-check mt-4">

                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isFeatured}
                  onChange={(e) =>
                    setIsFeatured(
                      e.target.checked
                    )
                  }
                />

                <label className="form-check-label">
                  Featured
                </label>
              </div>
            </div>

            {/* SEO SECTION */}

            <div className="col-12 mt-4">

              <h5>
                SEO Settings
              </h5>

            </div>

            <div className="col-md-6">

              <label className="form-label">
                Meta Title
              </label>

              <input
                className="form-control"
                value={metaTitle}
                onChange={(e) =>
                  setMetaTitle(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">

              <label className="form-label">
                Meta Keywords
              </label>

              <input
                className="form-control"
                value={metaKeywords}
                onChange={(e) =>
                  setMetaKeywords(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-12">

              <label className="form-label">
                Meta Description
              </label>

              <textarea
                className="form-control"
                rows={2}
                value={metaDescription}
                onChange={(e) =>
                  setMetaDescription(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">

              <label className="form-label">
                Canonical URL
              </label>

              <input
                className="form-control"
                value={canonicalUrl}
                onChange={(e) =>
                  setCanonicalUrl(
                    e.target.value
                  )
                }
              />
            </div>

            {/* OG IMAGE */}

            <div className="col-md-6">

              <label className="form-label">
                OG Image
              </label>

              <input
                type="file"
                className="form-control"
                onChange={(e) =>
                  setOgImage(
                    e.target.files?.[0] || null
                  )
                }
              />
            </div>

            <div className="col-md-6">

              <label className="form-label">
                OG Title
              </label>

              <input
                className="form-control"
                value={ogTitle}
                onChange={(e) =>
                  setOgTitle(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">

              <label className="form-label">
                OG Description
              </label>

              <input
                className="form-control"
                value={ogDescription}
                onChange={(e) =>
                  setOgDescription(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-12">

              <label className="form-label">
                Schema Markup
              </label>

              <textarea
                className="form-control"
                rows={4}
                value={schemaMarkup}
                onChange={(e) =>
                  setSchemaMarkup(
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* BUTTONS */}

          <div className="d-flex gap-2 mt-4">

            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!categoryName.trim()}
            >
              {editingCategoryId ? "Update Category" : "Save Category"}
            </button>

            <button
              className="btn btn-secondary"
              onClick={resetForm}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ========================================= */}
      {/* TABLE */}
      {/* ========================================= */}

      <div className="card border-0 shadow-sm">

        <div className="card-body">

          <DataTable<Category>

            title="Categories"

            rows={categories}

            rowKey={(r) =>
              r.categoryID.toString()
            }

            searchPlaceholder="Search..."

            exportFileName="categories"

            columns={[

              {
                key: "categoryID",
                label: "ID"
              },

              {
                key: "categoryImage",
                label: "Image",

                render: (r) => (

                  <img
                    src={
                      r.categoryImage
                        ? `${API_PATH}/${r.categoryImage}`
                        : "https://via.placeholder.com/50"
                    }
                    width={50}
                    height={50}
                    className="rounded"
                    alt="Category"
                  />
                )
              },

              {
                key: "categoryName",
                label: "Name"
              },

              {
                key: "categorySlug",
                label: "Slug",

                render: (r) => (
                  <code>
                    {r.categorySlug}
                  </code>
                )
              },

              {
                key: "status",
                label: "Status",

                render: (r) => (

                  <span
                    className={`badge ${r.status === "active"
                      ? "bg-success"
                      : "bg-danger"
                      }`}
                  >
                    {r.status}
                  </span>
                )
              },

              {
                key: "createdDate",
                label: "Created",
                render: (r) =>
                  r.createdDate
                    ? new Date(r.createdDate)
                      .toLocaleDateString("en-GB")
                    : "-"
              },

              {
                key: "actions",
                label: "Actions",

                render: (r) => (

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => setViewCategory(r)}
                    >
                      <i className="bi bi-eye"></i>
                    </button>

                    {/* EDIT BUTTON */}

                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => editCategory(r)}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </button>

                    {/* TOGGLE STATUS */}

                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() =>
                        toggleStatus(r)
                      }
                    >
                      Toggle
                    </button>

                    {/* DELETE */}
                    {Boolean(r.isActive) && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() =>
                          setDeleteConfirmId(
                            r.categoryID
                          )
                        }
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </div>
                )
              }

            ]}
          />
        </div>
      </div>


      {/* ========================================= */}
      {/* DELETE CONFIRM MODAL */}
      {/* ========================================= */}

      {deleteConfirmId && (

        <div
          className="pm-del-overlay"
          onClick={(e) => {

            if (e.target === e.currentTarget) {

              setDeleteConfirmId(null);
            }
          }}
        >

          <div className="pm-del-box">

            <div className="pm-del-ico">

              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
              >
                <polyline points="3 6 5 6 21 6" />

                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                <path d="M10 11v6" />

                <path d="M14 11v6" />
              </svg>

            </div>

            <div className="pm-del-title">
              Delete Category?
            </div>

            <div className="pm-del-sub">
              This action cannot be undone.
              The category will be permanently removed.
            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteConfirmId(null)
                }
                type="button"
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={deleteCategory}
                type="button"
              >
                Delete
              </button>

            </div>

          </div>

        </div>
      )}

      {/* ========================================= */}
      {/* VIEW MODAL */}
      {/* ========================================= */}

      {viewCategory && (
        <div
          className="ns-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setViewCategory(null); }}
        >
          <div className="ns-modal" style={{ maxWidth: 640 }}>

            {/* HEADER */}
            <div className="ns-modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: "linear-gradient(135deg,#ec4899,#8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <i className="bi bi-tags" style={{ color: "#fff", fontSize: 16 }}></i>
                </div>
                <div>
                  <div className="ns-modal-title">Category Details</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>ID #{viewCategory.categoryID}</div>
                </div>
              </div>
              <button className="ns-modal-close" onClick={() => setViewCategory(null)}>✕</button>
            </div>

            {/* BODY */}
            <div className="ns-modal-body">

              {/* IMAGE + BASIC INFO BANNER */}
              <div style={{
                display: "flex", alignItems: "center", gap: 16, padding: "16px",
                background: "linear-gradient(135deg,#fdf4ff,#faf5ff)",
                borderRadius: 12, marginBottom: 20, border: "1px solid #f3e8ff",
              }}>
                <img
                  src={
                    viewCategory.categoryImage
                      ? `${API_PATH}/${viewCategory.categoryImage}`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(viewCategory.categoryName)}&background=ec4899&color=fff&size=80&rounded=true`
                  }
                  alt="Category"
                  style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 17, color: "#1e293b", marginBottom: 4 }}>
                    {viewCategory.categoryName}
                  </div>
                  <code style={{ fontSize: 12, color: "#8b5cf6", background: "#f3e8ff", padding: "2px 8px", borderRadius: 6 }}>
                    /{viewCategory.categorySlug}
                  </code>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: viewCategory.status === "active" ? "#dcfce7" : "#fee2e2",
                      color: viewCategory.status === "active" ? "#166534" : "#991b1b",
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: viewCategory.status === "active" ? "#22c55e" : "#ef4444", display: "inline-block" }}></span>
                      {viewCategory.status}
                    </span>
                    {viewCategory.isFeatured && (
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>
                        ⭐ Featured
                      </span>
                    )}
                    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#f1f5f9", color: "#475569" }}>
                      Order #{viewCategory.displayOrder}
                    </span>
                  </div>
                </div>
              </div>

              {/* DESCRIPTION */}
              {viewCategory.description && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Description</div>
                  <div style={{ fontSize: 13, color: "#475569", background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9", lineHeight: 1.6 }}>
                    {viewCategory.description}
                  </div>
                </div>
              )}

              {/* SEO SECTION */}
              {(viewCategory.metaTitle || viewCategory.metaDescription || viewCategory.metaKeywords || viewCategory.canonicalUrl) && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="bi bi-search" style={{ fontSize: 12 }}></i> SEO Settings
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Meta Title", value: viewCategory.metaTitle },
                      { label: "Meta Keywords", value: viewCategory.metaKeywords },
                      { label: "Canonical URL", value: viewCategory.canonicalUrl, full: true },
                      { label: "Meta Description", value: viewCategory.metaDescription, full: true },
                    ].filter(f => f.value).map(field => (
                      <div key={field.label} style={{ gridColumn: field.full ? "1 / -1" : undefined, background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: .5, marginBottom: 3 }}>{field.label}</div>
                        <div style={{ fontSize: 13, color: "#334155", wordBreak: "break-word" }}>{field.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* OG SECTION */}
              {(viewCategory.ogTitle || viewCategory.ogDescription) && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="bi bi-share" style={{ fontSize: 12 }}></i> Open Graph
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "OG Title", value: viewCategory.ogTitle },
                      { label: "OG Description", value: viewCategory.ogDescription, full: true },
                    ].filter(f => f.value).map(field => (
                      <div key={field.label} style={{ gridColumn: field.full ? "1 / -1" : undefined, background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: .5, marginBottom: 3 }}>{field.label}</div>
                        <div style={{ fontSize: 13, color: "#334155" }}>{field.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SCHEMA MARKUP */}
              {viewCategory.schemaMarkup && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="bi bi-code-slash" style={{ fontSize: 12 }}></i> Schema Markup
                  </div>
                  <pre style={{
                    background: "#0f172a", color: "#e2e8f0", borderRadius: 10,
                    padding: "14px 16px", fontSize: 12, whiteSpace: "pre-wrap",
                    wordBreak: "break-word", margin: 0, maxHeight: 160, overflowY: "auto",
                  }}>
                    {viewCategory.schemaMarkup}
                  </pre>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="ns-modal-foot">
              <button
                type="button"
                onClick={() => setViewCategory(null)}
                style={{
                  background: "#f8fafc",
                  color: "#475569",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 10,
                  padding: "9px 20px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => { editCategory(viewCategory); setViewCategory(null); }}
                style={{
                  background: "linear-gradient(135deg,#ec4899,#8b5cf6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "9px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 8px rgba(139,92,246,.35)",
                }}
              >
                <i className="bi bi-pencil-square"></i>
                Edit Category
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Info / Success / Error Modal ── */}
      {infoModal && (
        <div className="pm-del-overlay" onClick={() => setInfoModal(null)}>
          <div className="pm-del-box">
            <div className="pm-del-ico" style={{
              background: infoModal.type === "success" ? "#f0fdf4" : infoModal.type === "warn" ? "#fefce8" : "#fef2f2"
            }}>
              {infoModal.type === "success" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              )}
              {infoModal.type === "warn" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
              )}
              {infoModal.type === "error" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              )}
            </div>
            <div className="pm-del-title" style={{
              color: infoModal.type === "success" ? "#166534" : infoModal.type === "warn" ? "#854d0e" : "#991b1b"
            }}>
              {infoModal.msg}
            </div>
            <div className="pm-del-btns">
              <button className="pm-btn-ghost" onClick={() => setInfoModal(null)} type="button">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}